import { useTranslation } from 'react-i18next';
import {
	Typography,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
	useDeleteMomentDetailExerciseMutation,
	useGetMomentDetailListQuery,
} from '@/api/app-setting/momentApiSlice';
import {
	MomentDetailPageDTO,
	MomentDetailPageFilter,
} from '@/api/DTO/app-setting/moment.interface';
import AddExerciseDialog from './components/AddExerciseDialog';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { OnoffType } from '@/api/enum/common.enum';
import { showLabel } from '@/utils/helper';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import CloseIcon from '@mui/icons-material/Close';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import SetDetailOrderDialog from './components/SetDetailOrderDialog';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}

export default function MomentDetailListSummaryPage() {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { momentId } = useParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const [deleteMomentDetailExercise] = useDeleteMomentDetailExerciseMutation();
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const navigate = useNavigate();

	const showModal = useModal();

	const onoffList = [
		{
			id: OnoffType.off,
			name: t('moment.off'),
		},
		{
			id: OnoffType.on,
			name: t('moment.on'),
		},
	];

	const exerciseTypeList = [
		{
			id: 1,
			name: t('moment.meditation'),
		},
		{
			id: 2,
			name: t('moment.body'),
		},
		{
			id: 3,
			name: t('moment.crossover'),
		},
	];

	const defaultPropertyFilter: MomentDetailPageFilter = {
		pagination: initialPagination,
		needNowId: momentId || '',
		sortings: [],
	};
	const defaultValues: DefaultValues<MomentDetailPageFilter> =
		defaultPropertyFilter;
	const methods = useForm<MomentDetailPageFilter>({
		defaultValues,
	});
	const transformedSearchParams: MomentDetailPageFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'needNowId',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetMomentDetailListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: MomentDetailPageDTO[]) => {
		if (response == null) return;
		const newResponse: DataGrid[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as MomentDetailPageFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const getLink = (row: any) => {
		if (row.exerciseType == 1) {
			return `/exercise-manage/meditation-list/info/${row.seriesId}/detail/${row.exerciseId}`;
		}
		if (row.exerciseType == 2) {
			return `/exercise-manage/body-list/info/${row.seriesId}/detail/${row.exerciseId}`;
		}
		if (row.exerciseType == 3) {
			return `/exercise-manage/crossover-list/info/${row.seriesId}/detail/${row.exerciseId}`;
		}
		return '';
	};

	const columns: GridColDef[] = [
		{
			field: 'titleZh',
			headerName: t('moment.exerciseTitleZh'),
			flex: 21,
		},
		{
			field: 'exerciseType',
			headerName: t('moment.exerciseType'),
			flex: 21,
			renderCell: (params: any) =>
				showLabel(params.row.exerciseType, exerciseTypeList),
		},
		{
			field: 'seriesTitleZh',
			headerName: t('moment.seriesTitleZh'),
			flex: 21,
		},
		{
			field: 'exerciseTime',
			headerName: t('moment.seriesTime'),
			flex: 21,
		},
		{
			field: 'seriesPublished',
			headerName: t('moment.seriesStatus'),
			flex: 21,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.seriesPublished == OnoffType.on
							? 'chipPrimary'
							: 'default'
					}
					label={showLabel(params.row.seriesPublished, onoffList)}
				/>
			),
		},
		{
			field: 'published',
			headerName: t('moment.pushStatus'),
			flex: 21,
			renderCell: (params: any) => (
				<Chip
					color={
						params.row.published == OnoffType.on ? 'chipPrimary' : 'default'
					}
					label={showLabel(params.row.published, onoffList)}
				/>
			),
		},
		{
			field: 'updatedAt',
			headerName: t('moment.lastUpdateTime'),
			flex: 21,
		},
		{
			field: 'createdAt',
			headerName: t('moment.createTime'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<IconButton
					color='primary'
					onClick={() => {
						const path = getLink(params.row);
						const url = window.location.origin + path;
						window.open(url, '_blank');
					}}
				>
					<VisibilityOutlinedIcon />
				</IconButton>
			),
		},
	];

	const handleSelect = (rows: any[]) => {
		setSelectedRows(rows);
	};

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('moment.deleteExercise')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('moment.isDeleteExercise')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteMomentDetailExercise(selectedRows);
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteMomentSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						setSelectedRows([]);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h6'>{t('moment.exerciseList')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<Button
						disabled={selectedRows.length < 1}
						variant='contained'
						style={{ alignItems: 'center' }}
						endIcon={<DeleteOutlined />}
						onClick={() => handleDelete()}
					>
						{t('button.delete')}
					</Button>
					<SetDetailOrderDialog />
					<AddExerciseDialog />
				</Stack>
			</Box>

			<FormProvider {...methods}>
				<Box marginTop={2}>
					<AppDataGrid
						loading={!isGetPropertySummarySuccess}
						rows={transformResponse(propertySummary?.data?.items) ?? []}
						rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
						columns={columns}
						getRowId={(row) => row.id}
						onSelectionModelChange={handleSelect}
						checkboxSelection
						selectionModel={selectedRows}
					/>
				</Box>
			</FormProvider>
		</>
	);
}
